import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow, useHydratedTranslation } from "@/components/marketing/Layout";
import { INDUSTRIES } from "@/lib/industries";
import { useAutoTranslate } from "@/components/AutoTranslate";
import { ArrowRight } from "lucide-react";

// Hub for the industry pages. With fourteen of them the internal linking
// matters as much as the pages themselves: this is what lets a crawler (and a
// visitor) find the one that applies to them.

export const Route = createFileRoute("/industries/")({
  head: () =>
    buildSeo({
      path: "/industries",
      title: "AI Twins by industry | Reality Twin",
      description:
        "How an AI Twin works in your industry: manufacturing, trades, real estate, healthcare, logistics, retail, finance, agencies and SaaS.",
    }),
  component: IndustriesHub,
});

function IndustriesHub() {
  const t = useHydratedTranslation();
  const { out } = useAutoTranslate(INDUSTRIES.flatMap((i) => [i.name, i.metaDescription]));
  let n = 0;
  const items = INDUSTRIES.map((i) => ({
    slug: i.slug,
    name: out[n++] ?? i.name,
    desc: out[n++] ?? i.metaDescription,
  }));

  return (
    <MarketingLayout>
      <section className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 pt-20 pb-12 sm:pt-28">
          <Eyebrow>{t("routes.industriesHub.eyebrow", { defaultValue: "By industry" })}</Eyebrow>
          <h1 className="mt-4 font-serif text-[40px] sm:text-[60px] leading-[1.01] tracking-[-0.035em]">
            {t("routes.industriesHub.title", {
              defaultValue: "Every business is being read by AI.",
            })}
          </h1>
          <p className="mt-5 text-[17px] leading-[1.6] text-muted-foreground max-w-2xl">
            {t("routes.industriesHub.sub", {
              defaultValue:
                "Buyers now ask an assistant before they ask you. What that assistant says about your company depends on what it can read. Pick your line of business to see what changes.",
            })}
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((i) => (
              <Link
                key={i.slug}
                to="/industries/$slug"
                params={{ slug: i.slug }}
                className="group hairline rounded-2xl bg-card p-6 hover:bg-cream transition flex flex-col"
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-serif text-[21px] leading-tight tracking-[-0.02em]">
                    {i.name}
                  </h2>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 mt-1 text-muted-foreground group-hover:translate-x-0.5 transition"
                    strokeWidth={2}
                  />
                </div>
                <p className="mt-2.5 text-[13.5px] leading-relaxed text-muted-foreground line-clamp-3">
                  {i.desc}
                </p>
              </Link>
            ))}
          </div>

          <div className="mt-10 hairline rounded-2xl bg-cream p-6 sm:p-8">
            <h2 className="font-serif text-[24px] sm:text-[28px] leading-tight tracking-[-0.02em]">
              {t("routes.industriesHub.missingTitle", {
                defaultValue: "Your industry isn't listed?",
              })}
            </h2>
            <p className="mt-2.5 text-[15px] text-muted-foreground max-w-2xl">
              {t("routes.industriesHub.missingBody", {
                defaultValue:
                  "The twin learns from your own website and documents, so it doesn't need a template for your sector. Run the free check and see how AI reads your company today.",
              })}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-2">
              <Link
                to="/twin-check"
                className="inline-flex justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
              >
                {t("routes.industriesHub.cta", {
                  defaultValue: "Check your AI-readability — free",
                })}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex justify-center items-center rounded-full hairline bg-background px-5 py-3 text-[14px] font-medium hover:bg-card transition"
              >
                {t("routes.industriesHub.talk", { defaultValue: "Talk to us" })}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
